/**
 * Branchen-Herkunft einer Registrierung.
 *
 * Die Branchen-Landingpages (`/rechtsanwaelte-start/`, `/handwerk-start/`, …) verlinken
 * ihren CTA als `/register?utm_source=fb&utm_medium=cpc&utm_campaign=signup&branche=<slug>-start`.
 * Bis hierher hat die Registrierungsseite diesen Parameter **verworfen**:
 *   - Die Branche war nach dem Klick nicht mehr sichtbar (kein Bezug zum Anliegen des Nutzers).
 *   - In Matomo gab es keine Zuordnung „welche Branchenseite bringt Registrierungen" —
 *     Ziel 1 „Registrierung" zählte nur die Summe.
 *
 * Diese Datei macht den Parameter auswertbar: Slug -> lesbares Label plus Merkfunktion
 * (sessionStorage), damit die Herkunft auch dann noch gilt, wenn der Nutzer vor dem
 * Absenden noch auf eine andere Seite (Datenschutz, Impressum) wechselt.
 */

export interface RegistrationBranch {
  /** Rohwert aus der URL, z. B. `rechtsanwaelte-start`. */
  slug: string;
  /** Lesbares Label für UI und Matomo, z. B. `Rechtsanwälte`. */
  label: string;
}

/** Lesbare Labels für alle Branchen-Landingpages mit CTA auf `/register`. */
export const BRANCH_LABELS: Record<string, string> = {
  'arztpraxis-start': 'Arztpraxen',
  'bestattungsunternehmen-start': 'Bestattungsunternehmen',
  'fitnessstudios-start': 'Fitnessstudios',
  'fotografen-start': 'Fotografen',
  'handwerk-start': 'Handwerksbetriebe',
  'immobilien-start': 'Immobilienmakler & Hausverwaltung',
  'kfz-werkstatt-start': 'Kfz-Werkstätten',
  'logistik-start': 'Logistikbetriebe',
  'personalwesen-start': 'Personalwesen',
  'pflegedienste-start': 'Pflegedienste',
  'physiotherapie-start': 'Physiotherapien',
  'rechtsanwaelte-start': 'Rechtsanwälte',
  'steuerberater-start': 'Steuerberater',
  'tom-start': 'Technische und organisatorische Maßnahmen',
  'verarbeitungsverzeichnis-muster': 'Verarbeitungsverzeichnis (Muster und Beispiel)',
  'loeschkonzept-start': 'Löschkonzept',
  'dsfa-start': 'Datenschutz-Folgenabschätzung',
  'verarbeitungsverzeichnis-start': 'Verarbeitungsverzeichnis',
  'vereine-start': 'Vereine',
  'zahnaerzte-start': 'Zahnärzte',
  // Branchenseiten im Werkstatt-Angebot (kein `-start`-Suffix, nutzen das Workshop-Formular).
  architekten: 'Architekturbüros',
  baeckereien: 'Bäckereien',
  fahrschulen: 'Fahrschulen',
  fotografen: 'Fotografen',
  friseure: 'Friseure',
  heilpraktiker: 'Heilpraktiker',
  'kfz-werkstaetten': 'Kfz-Werkstätten',
  lohnbuchhalter: 'Lohnbuchhaltung',
  steuerberater: 'Steuerberater',
  tieraerzte: 'Tierärzte',
  zahnaerzte: 'Zahnärzte',
};

const STORAGE_KEY = 'dsgvoeasy_branche';

/** `branche` aus einem Query-String lesen. Leere/zu lange Werte werden verworfen. */
export function parseBranch(search: string): RegistrationBranch | null {
  let raw: string | null = null;
  try {
    raw = new URLSearchParams(search).get('branche');
  } catch {
    return null;
  }
  if (!raw) return null;
  const slug = raw.trim().toLowerCase().slice(0, 60);
  // Nur Slugs aus dem eigenen Namensschema zulassen (kein Freitext aus der URL in die UI).
  if (!/^[a-z0-9-]+$/.test(slug)) return null;
  return { slug, label: BRANCH_LABELS[slug] ?? prettify(slug) };
}

/** Fallback für neue Branchenseiten, solange kein Label hinterlegt ist. */
function prettify(slug: string): string {
  const base = slug.replace(/-start$/, '').replace(/-/g, ' ');
  if (!base) return 'Ihre Branche';
  return base.charAt(0).toUpperCase() + base.slice(1);
}

/** Herkunft für die Dauer der Sitzung merken. */
export function rememberBranch(branch: RegistrationBranch | null): void {
  if (!branch) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(branch));
  } catch {
    /* sessionStorage kann in Privatmodus blockiert sein – dann gilt nur die aktuelle Seite. */
  }
}

/** Gemerkte Herkunft lesen (z. B. wenn der Nutzer über /datenschutz zurückkommt). */
export function readRememberedBranch(): RegistrationBranch | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RegistrationBranch>;
    if (!parsed || typeof parsed.slug !== 'string' || typeof parsed.label !== 'string') return null;
    return { slug: parsed.slug, label: parsed.label };
  } catch {
    return null;
  }
}

/** Matomo-Event-Wert: das Label, sonst „unbekannt" (damit Direktaufrufe zählbar bleiben). */
export function branchEventValue(branch: RegistrationBranch | null): string {
  return branch?.label || 'ohne Branchenseite';
}
